import React, { useRef, useState, useMemo } from 'react';
import { Plus, Trash2, Copy, Move } from 'lucide-react';
import { calculateKarma, computeGridCellValues } from '../../utils/karmaCalc';

const VIEW_W = 600;
const VIEW_H = 700;
const SNAP = 10;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AUTO_FIELDS = [
  { value: 'day', label: 'Gün (xam)' },
  { value: 'month', label: 'Ay (xam)' },
  { value: 'year', label: 'İl (xam)' },
  { value: 'day_r', label: 'Gün (22-qaydası ilə)' },
  { value: 'month_r', label: 'Ay (22-qaydası ilə)' },
  { value: 'year_r', label: 'İl (22-qaydası ilə)' },
];

// All sphere IDs available as direct auto-source values (main + additional).
// Order matches the visual matrix layout.
const SPHERE_KEYS = [
  'A', 'B', 'V', 'G', 'D', 'E', 'Zh', 'Z', 'I', 'K', 'L', 'M', 'HRT', 'Man',
];

const SPHERE_AUTO_FIELDS = SPHERE_KEYS.flatMap((k) => [
  { value: k, label: `${k} (əsas)` },
  { value: `${k}1`, label: `${k}1 (əlavə)` },
]);

const newId = () => `c${Math.random().toString(36).slice(2, 8)}`;

const defaultCell = () => ({
  id: newId(),
  label: 'Yeni',
  source: 'formula',
  auto_field: 'day',
  formula: '',
  description: '',
  source2: 'none',
  auto_field2: 'day',
  formula2: '',
  date_source: 'primary',
  bg_image: '',
  x: 250,
  y: 280,
  w: 80,
  h: 80,
  color: '#1f2937',
  text_color: '#ffffff',
  border_color: '#fbbf24',
  show_label: true,
  enabled: true,
});

const GridCellsEditor = ({ cells, nodeLabels, backgroundLayers, gridBgImage, gridTitle, gridPopupsEnabled, onChange, onBgImageChange, onTitleChange, onPopupsEnabledChange }) => {
  const wrapRef = useRef(null);
  const [dragging, setDragging] = useState(null); // { idx, mode: 'move'|'resize' }
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [previewDate, setPreviewDate] = useState('18.04.1991');
  const [previewDate2, setPreviewDate2] = useState('28.04.1990');
  const [snapEnabled, setSnapEnabled] = useState(true);

  const list = Array.isArray(cells) ? cells : [];

  const update = (next) => onChange(next);

  const updateOne = (idx, patch) => {
    const next = list.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    update(next);
  };

  const addCell = () => {
    const c = defaultCell();
    update([...list, c]);
    setSelectedIdx(list.length);
  };

  const dupCell = (idx) => {
    const src = list[idx];
    if (!src) return;
    const c = { ...src, id: newId(), x: src.x + 20, y: src.y + 20 };
    const next = [...list.slice(0, idx + 1), c, ...list.slice(idx + 1)];
    update(next);
    setSelectedIdx(idx + 1);
  };

  const delCell = (idx) => {
    if (!window.confirm('Bu hüceyrəni silmək istəyirsiniz?')) return;
    const next = list.filter((_, i) => i !== idx);
    update(next);
    setSelectedIdx(Math.max(0, idx - 1));
  };

  // Preview values
  const previewValues = useMemo(() => {
    const m = previewDate.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!m) return { results: null, cells: {} };
    const d = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const y = parseInt(m[3], 10);
    const results = calculateKarma(d, mo, y);
    const m2 = previewDate2.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    let extras = null;
    if (m2) {
      const d2 = parseInt(m2[1], 10);
      const mo2 = parseInt(m2[2], 10);
      const y2 = parseInt(m2[3], 10);
      extras = { results2: calculateKarma(d2, mo2, y2), day: d, month: mo, year: y, day2: d2, month2: mo2, year2: y2 };
    }
    const cellValues = computeGridCellValues(list, results, d, mo, y, extras);
    return { results, cells: cellValues, day: d, month: mo, year: y, hasDate2: !!m2 };
  }, [previewDate, previewDate2, list]);

  // Build value source dropdown options from node labels (for formula hints)
  const sphereTokenHints = useMemo(() => {
    const opts = ['day', 'month', 'year', 'day_r', 'month_r', 'year_r'];
    Object.entries(nodeLabels || {}).forEach(([id]) => {
      opts.push(id);
      opts.push(`${id}1`);
    });
    list.forEach((c) => opts.push(`cell_${c.id}`));
    return opts;
  }, [nodeLabels, list]);

  const snap = (v) => (snapEnabled ? Math.round(v / SNAP) * SNAP : Math.round(v));

  const onPointerDown = (idx, mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging({ idx, mode });
    setSelectedIdx(idx);
  };

  const onPointerMove = (e) => {
    if (!dragging || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const cx = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    const cy = ((e.clientY - rect.top) / rect.height) * VIEW_H;
    const cell = list[dragging.idx];
    if (!cell) return;
    if (dragging.mode === 'move') {
      const nx = snap(Math.max(0, Math.min(VIEW_W - cell.w, cx - cell.w / 2)));
      const ny = snap(Math.max(0, Math.min(VIEW_H - cell.h, cy - cell.h / 2)));
      updateOne(dragging.idx, { x: nx, y: ny });
    } else if (dragging.mode === 'resize') {
      const nw = Math.max(20, Math.min(400, snap(cx - cell.x)));
      const nh = Math.max(20, Math.min(400, snap(cy - cell.y)));
      updateOne(dragging.idx, { w: nw, h: nh });
    }
  };

  const onPointerUp = () => setDragging(null);

  const selected = list[selectedIdx];

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Hüceyrə Şəbəkəsi (kvadrat formada)</h3>
      <p className="admin-section-desc">
        Kvadrat hüceyrələr əlavə edin, sürüklə-buraxla yerini dəyişin. Hər hüceyrə ya doğum tarixindən
        avtomatik dəyər götürür, ya da formul vasitəsilə (məs: <code>A+B-cell_x1</code>, <code>day+month_r</code>) hesablanır.
        Bu bölmə &quot;grid&quot; tipi olaraq ana səhifədə &quot;Bölmələr&quot;-ə əlavə edilə bilər.
      </p>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button onClick={addCell} type="button" className="admin-btn-primary text-xs px-3 py-2 inline-flex items-center gap-1"
          data-testid="grid-add-cell">
          <Plus size={14} /> Hüceyrə əlavə et
        </button>
        <label className="flex items-center gap-2 text-xs text-white/70">
          <input type="checkbox" checked={snapEnabled} onChange={(e) => setSnapEnabled(e.target.checked)} />
          Snap-to-grid (10px)
        </label>
        <span className="text-xs text-white/50 ml-auto">Hüceyrə sayı: {list.length}</span>
      </div>

      {/* Canvas-level settings: title + background image (applied to the whole snap-to-grid) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 p-3 rounded border border-yellow-500/20 bg-yellow-500/5">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-yellow-300/80">Şəbəkə başlığı (canvas üstündə görünür)</span>
          <input className="karma-input text-left"
            value={gridTitle || ''}
            onChange={(e) => onTitleChange && onTitleChange(e.target.value)}
            placeholder="məs: Çocukluk Kodu Cədvəli"
            data-testid="grid-canvas-title" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-yellow-300/80">Şəbəkə arxa fon şəkli</span>
          <div className="flex items-center gap-2">
            <select
              className="karma-input flex-1"
              value={gridBgImage || ''}
              onChange={(e) => onBgImageChange && onBgImageChange(e.target.value)}
              data-testid="grid-canvas-bgimage">
              <option value="">— (şəkilsiz)</option>
              {(backgroundLayers || []).map((layer, i) => (
                <option key={`${layer.filename}-${i}`} value={layer.filename}>
                  {layer.filename}
                </option>
              ))}
            </select>
            {gridBgImage && (
              <button type="button"
                onClick={() => onBgImageChange && onBgImageChange('')}
                className="text-xs px-2 py-1 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10">
                Sil
              </button>
            )}
          </div>
        </label>
        <label className="flex items-center gap-2 sm:col-span-2">
          <input type="checkbox"
            checked={gridPopupsEnabled !== false}
            onChange={(e) => onPopupsEnabledChange && onPopupsEnabledChange(e.target.checked)}
            data-testid="grid-popups-toggle" />
          <span className="text-xs text-yellow-300/80">
            Hüceyrələrə klikləndikdə şərh popup-larını göstər
            <span className="text-white/40 ml-2">(sönmüş olduqda "i" nöqtəsi görünmür, klik heç nə etmir)</span>
          </span>
        </label>
      </div>

      {/* Preview date */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-sm text-white/70">Önizləmə tarixi:</span>
        <input className="karma-input text-left" style={{ maxWidth: 160 }}
          value={previewDate}
          onChange={(e) => setPreviewDate(e.target.value)}
          placeholder="GG.AA.İİİİ"
          data-testid="grid-preview-date" />
        {previewValues.results && (
          <span className="text-xs text-yellow-300/70">
            day={previewValues.day} month={previewValues.month} year={previewValues.year}
          </span>
        )}
        <span className="text-sm text-white/70 ml-2">2-ci tarix (Σ üçün):</span>
        <input className="karma-input text-left" style={{ maxWidth: 160 }}
          value={previewDate2}
          onChange={(e) => setPreviewDate2(e.target.value)}
          placeholder="GG.AA.İİİİ"
          data-testid="grid-preview-date2" />
      </div>

      {/* SVG canvas */}
      <div
        ref={wrapRef}
        className="position-editor-canvas"
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{
          position: 'relative',
          aspectRatio: `${VIEW_W} / ${VIEW_H}`,
          background: 'rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8,
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} style={{ width: '100%', height: '100%' }}>
          {/* Canvas-level background image (under cells) */}
          {gridBgImage && (
            <image
              href={gridBgImage.startsWith('http') ? gridBgImage : `${BACKEND_URL}/api/uploads/${gridBgImage}`}
              x="0" y="0" width={VIEW_W} height={VIEW_H}
              preserveAspectRatio="xMidYMid slice"
              opacity="0.7"
            />
          )}
          {/* Subtle grid pattern */}
          <defs>
            <pattern id="gridcells-grid" width={SNAP * 5} height={SNAP * 5} patternUnits="userSpaceOnUse">
              <path d={`M ${SNAP * 5} 0 L 0 0 0 ${SNAP * 5}`} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width={VIEW_W} height={VIEW_H} fill="url(#gridcells-grid)" />

          {/* Canvas-level title */}
          {gridTitle && (
            <text
              x={VIEW_W / 2} y={40}
              textAnchor="middle"
              fill="#fbbf24"
              fontSize="28"
              fontWeight="800"
              fontFamily="'Cinzel', serif"
              style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.85)', strokeWidth: 1.5 }}
            >
              {gridTitle}
            </text>
          )}

          {list.map((c, idx) => {
            const isSel = idx === selectedIdx;
            const r = previewValues.cells[c.id] || {};
            const v1 = r.v1;
            const v2 = r.v2;
            const has1 = v1 !== undefined && v1 !== null;
            const has2 = v2 !== undefined && v2 !== null;
            const display = has1 && has2 ? `${v1} / ${v2}` : (has1 ? `${v1}` : (has2 ? `${v2}` : '?'));
            const isTitleOnly = c.source === 'none' && (c.source2 || 'none') === 'none';
            return (
              <g
                key={c.id}
                data-testid={`grid-cell-${c.id}`}
                style={{ cursor: 'grab' }}
                onPointerDown={onPointerDown(idx, 'move')}
              >
                <rect
                  x={c.x} y={c.y} width={c.w} height={c.h}
                  fill={c.color || '#1f2937'}
                  rx={4}
                  opacity={c.enabled ? 0.95 : 0.4}
                />
                <rect
                  x={c.x} y={c.y} width={c.w} height={c.h}
                  fill="none"
                  stroke={isSel ? '#22d3ee' : (c.border_color || '#fbbf24')}
                  strokeWidth={isSel ? 2.5 : 1.5}
                  rx={4}
                />
                {c.show_label && c.label && (
                  <text
                    x={c.x + c.w / 2}
                    y={c.y + (isTitleOnly ? c.h / 2 : 16)}
                    textAnchor="middle"
                    dominantBaseline={isTitleOnly ? 'central' : 'auto'}
                    fill={c.text_color || '#ffffff'}
                    fontSize={isTitleOnly ? Math.max(13, Math.min(c.w, c.h) * 0.32) : 13}
                    fontWeight={isTitleOnly ? '800' : '700'}
                    fontFamily="'Cinzel', serif"
                    style={{ pointerEvents: 'none', paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.55)', strokeWidth: 0.5 }}
                  >
                    {c.label || c.id}
                  </text>
                )}
                {!isTitleOnly && (
                  <text x={c.x + c.w / 2} y={c.y + c.h / 2 + (c.show_label ? 8 : 0)}
                    textAnchor="middle" dominantBaseline="central"
                    fill={c.text_color || '#ffffff'}
                    fontSize={Math.max(13, Math.min(c.w, c.h) * (has1 && has2 ? 0.32 : 0.42))}
                    fontWeight="800" fontFamily="'Inter', sans-serif"
                    style={{ pointerEvents: 'none', paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.55)', strokeWidth: 0.6 }}>
                    {display}
                  </text>
                )}
                {/* Resize handle */}
                {isSel && (
                  <rect
                    x={c.x + c.w - 8} y={c.y + c.h - 8}
                    width="12" height="12"
                    fill="#22d3ee" opacity="0.85"
                    style={{ cursor: 'nwse-resize' }}
                    onPointerDown={onPointerDown(idx, 'resize')}
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Cell list + edit panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
        {/* List */}
        <div className="lg:col-span-1">
          <h4 className="text-sm font-semibold text-white/80 mb-2">Hüceyrələr</h4>
          <div className="flex flex-col gap-1 max-h-80 overflow-y-auto">
            {list.length === 0 && (
              <div className="text-xs text-white/40 italic">Hələ heç bir hüceyrə yoxdur. &quot;Hüceyrə əlavə et&quot; düyməsinə basın.</div>
            )}
            {list.map((c, idx) => (
              <div
                key={c.id}
                onClick={() => setSelectedIdx(idx)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border ${idx === selectedIdx ? 'border-cyan-400 bg-cyan-500/10' : 'border-white/10 bg-black/30 hover:bg-white/5'}`}
                data-testid={`grid-cell-row-${c.id}`}
              >
                <div className="w-3 h-3 rounded" style={{ background: c.color }}></div>
                <div className="text-sm text-white/90 flex-1 truncate">
                  {c.label || c.id}
                  <span className="text-xs text-white/40 ml-1">({c.source === 'auto' ? c.auto_field : (c.formula || '—')})</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); dupCell(idx); }} className="p-1 text-white/60 hover:text-white" title="Dublikat">
                  <Copy size={12} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); delCell(idx); }} className="p-1 text-red-400/70 hover:text-red-300" title="Sil">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Edit panel */}
        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold text-white/80 mb-2">Seçilmiş hüceyrə</h4>
          {!selected ? (
            <div className="text-xs text-white/40 italic">Heç bir hüceyrə seçilməyib.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded border border-white/10 bg-black/30">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-white/60">Ad / Etiket</span>
                <input className="karma-input text-left"
                  value={selected.label || ''}
                  onChange={(e) => updateOne(selectedIdx, { label: e.target.value })}
                  data-testid="grid-cell-label" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-white/60">ID (formul-da istinad üçün: cell_{selected.id})</span>
                <input className="karma-input text-left font-mono text-xs"
                  value={selected.id}
                  onChange={(e) => {
                    const nid = e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 40);
                    if (!nid) return;
                    updateOne(selectedIdx, { id: nid });
                  }} />
              </label>

              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-white/60">Şərh / İzahat (istifadəçi hüceyrəyə klikləndikdə popup-da göstəriləcək)</span>
                <textarea className="karma-input text-left"
                  rows={3}
                  value={selected.description || ''}
                  onChange={(e) => updateOne(selectedIdx, { description: e.target.value })}
                  placeholder="Bu kod sizin uşaqlıq nöqtənizdir. Onun mənası..."
                  data-testid="grid-cell-description" />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs text-white/60">Mənbə (əsas dəyər)</span>
                <select className="karma-input"
                  value={selected.source}
                  onChange={(e) => updateOne(selectedIdx, { source: e.target.value })}
                  data-testid="grid-cell-source">
                  <option value="none">— (yalnız başlıq)</option>
                  <option value="formula">Formul</option>
                  <option value="auto">Avtomatik (tarix / sfera)</option>
                </select>
              </label>

              {selected.source === 'auto' ? (
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-white/60">Avtomatik sahə</span>
                  <select className="karma-input"
                    value={selected.auto_field}
                    onChange={(e) => updateOne(selectedIdx, { auto_field: e.target.value })}
                    data-testid="grid-cell-auto-field">
                    <optgroup label="Doğum tarixi">
                      {AUTO_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </optgroup>
                    <optgroup label="Sfera dəyərləri (əsas + əlavə)">
                      {SPHERE_AUTO_FIELDS.map((f) => {
                        const baseId = f.value.endsWith('1') ? f.value.slice(0, -1) : f.value;
                        const userLetter = (nodeLabels?.[baseId]?.letter || '').trim();
                        const labelPrefix = userLetter ? `${userLetter} · ` : '';
                        return (
                          <option key={f.value} value={f.value}>{labelPrefix}{f.label}</option>
                        );
                      })}
                    </optgroup>
                  </select>
                </label>
              ) : selected.source === 'formula' ? (
                <label className="flex flex-col gap-1 sm:col-span-2">
                  <span className="text-xs text-white/60">
                    Formul (məs: <code className="text-yellow-300">A+B-V</code>, <code className="text-yellow-300">day+month_r</code>, <code className="text-yellow-300">cell_{selected.id}+5</code>)
                  </span>
                  <input className="karma-input text-left font-mono"
                    value={selected.formula || ''}
                    onChange={(e) => updateOne(selectedIdx, { formula: e.target.value })}
                    placeholder="A+B-V"
                    data-testid="grid-cell-formula" />
                  <span className="text-[10px] text-white/40">
                    Tokenlər: {sphereTokenHints.slice(0, 20).join(', ')}{sphereTokenHints.length > 20 ? '…' : ''}
                  </span>
                </label>
              ) : (
                <div className="text-xs text-white/40 italic flex items-center">
                  Bu hüceyrə yalnız başlıq olaraq göstəriləcək.
                </div>
              )}

              {/* ====== Secondary value (rendered as "v1 / v2") ====== */}
              <label className="flex flex-col gap-1">
                <span className="text-xs text-white/60">Mənbə (əlavə dəyər)</span>
                <select className="karma-input"
                  value={selected.source2 || 'none'}
                  onChange={(e) => updateOne(selectedIdx, { source2: e.target.value })}
                  data-testid="grid-cell-source2">
                  <option value="none">— (yoxdur)</option>
                  <option value="formula">Formul</option>
                  <option value="auto">Avtomatik (tarix / sfera)</option>
                </select>
              </label>

              {(selected.source2 || 'none') === 'auto' ? (
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-white/60">Avtomatik sahə (əlavə)</span>
                  <select className="karma-input"
                    value={selected.auto_field2 || 'day'}
                    onChange={(e) => updateOne(selectedIdx, { auto_field2: e.target.value })}>
                    <optgroup label="Doğum tarixi">
                      {AUTO_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </optgroup>
                    <optgroup label="Sfera dəyərləri (əsas + əlavə)">
                      {SPHERE_AUTO_FIELDS.map((f) => {
                        const baseId = f.value.endsWith('1') ? f.value.slice(0, -1) : f.value;
                        const userLetter = (nodeLabels?.[baseId]?.letter || '').trim();
                        const labelPrefix = userLetter ? `${userLetter} · ` : '';
                        return (
                          <option key={f.value} value={f.value}>{labelPrefix}{f.label}</option>
                        );
                      })}
                    </optgroup>
                  </select>
                </label>
              ) : (selected.source2 || 'none') === 'formula' ? (
                <label className="flex flex-col gap-1 sm:col-span-2">
                  <span className="text-xs text-white/60">Formul (əlavə)</span>
                  <input className="karma-input text-left font-mono"
                    value={selected.formula2 || ''}
                    onChange={(e) => updateOne(selectedIdx, { formula2: e.target.value })}
                    placeholder="A1+B1" />
                </label>
              ) : null}

              {/* Which birth date this cell uses when "İl Enerji Uyumu" (dual
                  date mode) is active. Matches the custom_nodes contract. */}
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs text-yellow-300/80">
                  Tarix mənbəyi (İl Enerji Uyumu rejimi üçün)
                  <span className="text-white/40 ml-2">
                    (yalnız 2 tarix rejimi aktiv olduqda təsir edir)
                  </span>
                </span>
                <select className="karma-input"
                  value={selected.date_source || 'primary'}
                  onChange={(e) => updateOne(selectedIdx, { date_source: e.target.value })}
                  data-testid="grid-cell-date-source">
                  <option value="primary">1-ci tarix</option>
                  <option value="secondary">2-ci tarix</option>
                  <option value="both">Hər ikisi (hər iki bloka göstərilir)</option>
                  <option value="sum">Σ Cəm (1-ci + 2-ci tarix) → əsas/əlavə</option>
                </select>
                <span className="text-[10px] text-white/40">
                  Σ rejimində nəticə <code className="text-yellow-300">əsas/əlavə</code> formatında göstərilir: əsas dəyər 22-qaydası ilə (18+6=24→2), əlavə dəyər rəqəm cəmi ilə (18+10=28→10). Formula-da <code className="text-yellow-300">d1_HRT</code> · <code className="text-yellow-300">d2_HRT</code> · <code className="text-yellow-300">sum_HRT</code> tokenlərini də istifadə edə bilərsiniz.
                </span>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs text-white/60">X (sol)</span>
                <input type="number" className="karma-input text-left"
                  value={selected.x}
                  onChange={(e) => updateOne(selectedIdx, { x: parseFloat(e.target.value) || 0 })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-white/60">Y (üst)</span>
                <input type="number" className="karma-input text-left"
                  value={selected.y}
                  onChange={(e) => updateOne(selectedIdx, { y: parseFloat(e.target.value) || 0 })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-white/60">En</span>
                <input type="number" className="karma-input text-left"
                  value={selected.w}
                  onChange={(e) => updateOne(selectedIdx, { w: Math.max(20, parseFloat(e.target.value) || 80) })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-white/60">Hündürlük</span>
                <input type="number" className="karma-input text-left"
                  value={selected.h}
                  onChange={(e) => updateOne(selectedIdx, { h: Math.max(20, parseFloat(e.target.value) || 80) })} />
              </label>

              <label className="flex items-center gap-2">
                <span className="text-xs text-white/60 w-24">Fon rəngi</span>
                <input type="color"
                  value={selected.color || '#1f2937'}
                  onChange={(e) => updateOne(selectedIdx, { color: e.target.value })} />
              </label>
              <label className="flex items-center gap-2">
                <span className="text-xs text-white/60 w-24">Mətn rəngi</span>
                <input type="color"
                  value={selected.text_color || '#ffffff'}
                  onChange={(e) => updateOne(selectedIdx, { text_color: e.target.value })} />
              </label>
              <label className="flex items-center gap-2">
                <span className="text-xs text-white/60 w-24">Kənar rəngi</span>
                <input type="color"
                  value={selected.border_color || '#fbbf24'}
                  onChange={(e) => updateOne(selectedIdx, { border_color: e.target.value })} />
              </label>

              <label className="flex items-center gap-2">
                <input type="checkbox"
                  checked={selected.show_label !== false}
                  onChange={(e) => updateOne(selectedIdx, { show_label: e.target.checked })} />
                <span className="text-xs text-white/70">Etiket göstər</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox"
                  checked={selected.enabled !== false}
                  onChange={(e) => updateOne(selectedIdx, { enabled: e.target.checked })} />
                <span className="text-xs text-white/70">Aktiv</span>
              </label>

              {selected.source === 'formula' && (
                <div className="sm:col-span-2 text-xs text-cyan-300 bg-cyan-500/5 border border-cyan-500/20 rounded p-2 flex items-center gap-2">
                  <Move size={12} /> Önizləmə dəyəri:
                  <span className="font-bold">
                    {(() => {
                      const r = previewValues.cells[selected.id];
                      if (r && typeof r === 'object') {
                        const has2 = r.v2 !== null && r.v2 !== undefined;
                        return has2 ? `${r.v1 ?? '—'} / ${r.v2}` : (r.v1 ?? '—');
                      }
                      return r ?? '—';
                    })()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GridCellsEditor;
